import { ref, watch, computed, unref } from 'vue'
import { cloneDeep, isEqual, get } from 'lodash'

/**
 * useDirty - 監聽指定資料是否有變動
 *
 * @param {Object} data - 要監聽的響應式資料 (支援 reactive() 或 ref())
 * @returns 回傳包含 isDirty 和 dirtyFields 的物件
 *
 * @example
 * // 使用 reactive
 * const formData = reactive({
 *   name: undefined,
 *   phone: undefined,
 *   address: {
 *     city: undefined,
 *     detail: undefined,
 *   }
 * })
 *
 * // 使用 ref
 * const formData = ref({
 *   name: undefined,
 *   phone: undefined,
 * })
 *
 * const { isDirty, dirtyFields } = useDirty(formData)
 *
 * // isDirty: boolean 是否所有資料與原來完全一樣
 * // dirtyFields: (key: string) => boolean 檢查單一欄位是否已有變動
 * dirtyFields('address.detail') // false
 */
export default function useDirty (data) {
  // 儲存初始狀態的深拷貝，考慮 ref 和 reactive 的情況
  // 初始不設定 originalData，等 startWatch 被呼叫後才會設置
  const originalData = ref(null)

  // started flag 決定是否開始比對
  const started = ref(false)

  // 監聽資料變動，支援 ref 和 reactive
  watch(
    () => unref(data),
    () => {
      // 觸發計算屬性重新計算（但只有在 started 時才有實際意義）
    },
    { deep: true, immediate: false },
  )

  // 計算是否有任何變動
  const isDirty = computed(() => {
    // 如果還沒 start，視為沒有變動
    if (!started.value || originalData.value === null) return false
    return !isEqual(originalData.value, unref(data))
  })

  /**
   * 檢查特定欄位是否有變動
   * @param {string} fieldPath - 欄位路徑，例如 'name' 或 'address.city'
   * @returns {boolean} - 該欄位是否有變動
   */
  const dirtyFields = (fieldPath) => {
    // 若尚未開始比對，單一欄位視為未變動
    if (!started.value || originalData.value === null) return false
    const originalValue = get(originalData.value, fieldPath)
    const currentValue = get(unref(data), fieldPath)
    return !isEqual(originalValue, currentValue)
  }

  /**
   * 取得特定欄位的原始值
   * @param {string} fieldPath - 欄位路徑
   * @returns {*} - 原始值
   */
  const getOriginalValue = (fieldPath) => {
    if (originalData.value === null) return undefined
    return get(originalData.value, fieldPath)
  }

  /**
   * 重置 dirty tracking
   * 將當前資料設為新的原始值
   */
  const reset = () => {
    // 如果還沒 start，先不啟動比對，只更新 originalData
    originalData.value = cloneDeep(unref(data))
    // 當 reset 被呼叫時，也可視為開始比對（可依需求調整）
    started.value = true
  }

  /**
   * 開始監聽與比對
   * - 設定當前資料為原始值
   * - 將 started 旗標設為 true
   */
  const startWatch = () => {
    originalData.value = cloneDeep(unref(data))
    started.value = true
  }

  return {
    isDirty,
    dirtyFields,
    getOriginalValue,
    reset,
    startWatch,
  }
}
